import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { userApi, trainingApi, trainingProgramApi } from '../../api'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { UserRelation, TrainingSet, TrainingProgram, TrainingProgramActivation } from '../../types/api'
import { UserDetail } from './UserDetail'

export default function TraineeDetail() {
  const { id: traineeId } = useParams<{ id: string }>()

  const [trainee, setTrainee] = useState<UserRelation | null>(null)
  const [trainingSets, setTrainingSets] = useState<TrainingSet[]>([])
  const [coachPrograms, setCoachPrograms] = useState<TrainingProgram[]>([])
  const [traineeAssignment, setTraineeAssignment] = useState<TrainingProgramActivation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTraineeData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traineeId])

  async function loadTraineeData() {
    if (!traineeId) {
      setError('No trainee ID provided')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const trainees = await userApi.getTrainees()
      const foundTrainee = trainees.find(t => t.id === parseInt(traineeId))

      if (foundTrainee) {
        setTrainee(foundTrainee)

        const [sets, programs, assignment] = await Promise.all([
          trainingApi.getTraineeSets(foundTrainee.id),
          trainingProgramApi.getPrograms(),
          trainingProgramActivationApi.getTraineeActiveAssignment(foundTrainee.id).catch(() => null),
        ])
        setTrainingSets(sets)
        setCoachPrograms(programs)
        setTraineeAssignment(assignment)
      } else {
        setError('Trainee not found')
      }
    } catch (err) {
      console.error('Error loading trainee:', err)
      setError(err instanceof Error ? err.message : 'Failed to load trainee details')
    } finally {
      setLoading(false)
    }
  }

  if (!trainee && !loading && !error) {
    return null
  }

  return (
    <UserDetail
      user={trainee!}
      trainingSets={trainingSets}
      coachPrograms={coachPrograms}
      traineeAssignment={traineeAssignment}
      onAssignmentChange={setTraineeAssignment}
      onTrainingSetDeleted={(setId) => setTrainingSets(prev => prev.filter(s => s.id !== setId))}
      loading={loading}
      error={error}
      userType="trainee"
      backPath="/trainees"
      userId={traineeId || ''}
      onRetry={loadTraineeData}
    />
  )
}
